// =============================================================
//  AnalyticsScreen (web) — Tableau analytique complet
//  Stat cards + graphes : utilisateurs/mois, revenus/mois,
//  top documents, répartition par type, démographie.
// =============================================================

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

import AdminLayout from './web-ui/AdminLayout';
import {
  Card,
  StatCard,
  Badge,
  EmptyState,
  Loading,
  SegmentedSelect,
} from './web-ui/kit';
import { ChartCard, AreaSpark, BarSimple, PieDonut, LineDouble } from './web-ui/Charts';
import { colors, spacing } from './web-ui/theme';

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const FCFA = (n) => new Intl.NumberFormat('fr-FR').format(Number(n) || 0) + ' FCFA';
const num = (n) => new Intl.NumberFormat('fr-FR').format(Number(n) || 0);

const toDate = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (typeof ts === 'number') return new Date(ts);
  if (typeof ts === 'string') {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const buildLastNMonths = (n) => {
  const now = new Date();
  const arr = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    arr.push({ label: MONTHS[d.getMonth()], year: d.getFullYear(), month: d.getMonth() });
  }
  return arr;
};

const RANGES = [
  { value: '6', label: '6 mois' },
  { value: '12', label: '12 mois' },
  { value: '24', label: '24 mois' },
];

export default function AnalyticsScreen() {
  const [range, setRange] = useState('12');
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalDocs: 0,
    totalRevenue: 0,
    totalSales: 0,
  });
  const [usersByMonth, setUsersByMonth] = useState([]);
  const [revenueByMonth, setRevenueByMonth] = useState([]);
  const [topDocs, setTopDocs] = useState([]);
  const [typeBreakdown, setTypeBreakdown] = useState([]);
  const [genderBreakdown, setGenderBreakdown] = useState([]);
  const [regionBreakdown, setRegionBreakdown] = useState([]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const monthsCount = parseInt(range, 10) || 12;
      const months = buildLastNMonths(monthsCount);

      const [usersSnap, concoursSnap, bordsSnap, docsSnap, purchasesSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'concours')),
        getDocs(collection(db, 'bords')),
        getDocs(collection(db, 'documents')),
        getDocs(collection(db, 'purchases')).catch(() => ({ docs: [] })),
      ]);

      const allUsers = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const purchases = purchasesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Stats globales
      const totalDocs = concoursSnap.size + bordsSnap.size + docsSnap.size;
      const completed = purchases.filter(
        (p) => !p.status || ['completed', 'success', 'paid', 'successful'].includes(String(p.status).toLowerCase())
      );
      const totalRevenue = completed.reduce(
        (s, p) => s + (Number(p.amount) || Number(p.prix) || Number(p.prixFinal) || 0),
        0
      );

      // Utilisateurs/mois
      const userSeries = months.map((m) => ({ label: m.label, value: 0 }));
      allUsers.forEach((u) => {
        const d = toDate(u.createdAt);
        if (!d) return;
        const idx = months.findIndex((m) => m.year === d.getFullYear() && m.month === d.getMonth());
        if (idx >= 0) userSeries[idx].value += 1;
      });

      // Revenus/mois (MTN vs Orange)
      const revSeries = months.map((m) => ({ label: m.label, mtn: 0, orange: 0 }));
      completed.forEach((p) => {
        const d = toDate(p.purchaseDate || p.createdAt);
        if (!d) return;
        const idx = months.findIndex((m) => m.year === d.getFullYear() && m.month === d.getMonth());
        if (idx < 0) return;
        const amount =
          Number(p.amount) || Number(p.prix) || Number(p.prixFinal) || 0;
        const op = String(p.paymentMethod || p.operator || '').toLowerCase();
        if (op.includes('orange')) revSeries[idx].orange += amount;
        else revSeries[idx].mtn += amount;
      });

      // Top documents
      const docCounts = {};
      completed.forEach((p) => {
        const key = p.titre || p.documentTitle || p.documentId || '—';
        docCounts[key] = (docCounts[key] || 0) + 1;
      });
      const tops = Object.entries(docCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8)
        .map(([label, value]) => ({ label: label.slice(0, 28), value }));

      // Répartition par type
      const breakdown = [
        { label: 'Concours', value: concoursSnap.size },
        { label: 'Bords', value: bordsSnap.size },
        { label: 'Documents', value: docsSnap.size },
      ].filter((x) => x.value > 0);

      // Démographie utilisateur
      const genderMap = {};
      const regionMap = {};
      allUsers.forEach((u) => {
        const g = (u.gender || 'Non précisé').trim() || 'Non précisé';
        genderMap[g] = (genderMap[g] || 0) + 1;
        const r = (u.region || 'Non précisé').trim() || 'Non précisé';
        regionMap[r] = (regionMap[r] || 0) + 1;
      });
      const genders = Object.entries(genderMap)
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value);
      const regions = Object.entries(regionMap)
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8);

      setStats({
        totalUsers: usersSnap.size,
        activeUsers: allUsers.filter((u) => u.status === 'active' || !u.status).length,
        totalDocs,
        totalRevenue,
        totalSales: completed.length,
      });
      setUsersByMonth(userSeries);
      setRevenueByMonth(revSeries);
      setTopDocs(tops);
      setTypeBreakdown(breakdown);
      setGenderBreakdown(genders);
      setRegionBreakdown(regions);
    } catch (e) {
      console.log('analytics load error', e?.message || e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const averageBasket = useMemo(() => {
    if (!stats.totalSales) return 0;
    return Math.round(stats.totalRevenue / stats.totalSales);
  }, [stats]);

  return (
    <AdminLayout
      title="Analytics"
      subtitle="Vue analytique détaillée de la plateforme MyConcours"
    >
      <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: spacing.lg }}>
        <Card style={{ padding: 8 }}>
          <SegmentedSelect options={RANGES} value={range} onChange={setRange} />
        </Card>
      </View>

      <View style={styles.statsRow}>
        <StatCard
          label="Utilisateurs"
          value={num(stats.totalUsers)}
          icon="account-group"
          accent={colors.primary}
          loading={loading}
        />
        <StatCard
          label="Comptes actifs"
          value={num(stats.activeUsers)}
          icon="account-check-outline"
          accent={colors.success}
          loading={loading}
        />
        <StatCard
          label="Documents publiés"
          value={num(stats.totalDocs)}
          icon="file-document-multiple"
          accent={colors.info}
          loading={loading}
        />
        <StatCard
          label="Ventes"
          value={num(stats.totalSales)}
          icon="cart-outline"
          accent={colors.warning}
          loading={loading}
        />
        <StatCard
          label="Revenus totaux"
          value={loading ? '—' : FCFA(stats.totalRevenue)}
          icon="cash-multiple"
          accent={colors.success}
          loading={loading}
        />
        <StatCard
          label="Panier moyen"
          value={loading ? '—' : FCFA(averageBasket)}
          icon="basket-outline"
          accent={colors.primary}
          loading={loading}
        />
      </View>

      <View style={styles.chartsRow}>
        <View style={{ flex: 2, minWidth: 320 }}>
          <ChartCard
            title="Croissance des utilisateurs"
            subtitle={`${range} derniers mois`}
            height={280}
          >
            {loading ? (
              <Loading />
            ) : usersByMonth.length > 0 ? (
              <AreaSpark data={usersByMonth} dataKey="value" color={colors.primary} />
            ) : (
              <EmptyState icon="account-off-outline" title="Pas de données" />
            )}
          </ChartCard>
        </View>
        <View style={{ flex: 1, minWidth: 280 }}>
          <ChartCard title="Documents par type" subtitle="Catalogue actuel" height={280}>
            {loading ? (
              <Loading />
            ) : typeBreakdown.length > 0 ? (
              <PieDonut data={typeBreakdown} />
            ) : (
              <EmptyState icon="chart-pie" title="Catalogue vide" />
            )}
          </ChartCard>
        </View>
      </View>

      <View style={styles.chartsRow}>
        <View style={{ flex: 2, minWidth: 320 }}>
          <ChartCard
            title="Revenus mensuels"
            subtitle="MTN MoMo vs Orange Money"
            height={280}
          >
            {loading ? (
              <Loading />
            ) : revenueByMonth.some((d) => d.mtn || d.orange) ? (
              <LineDouble
                data={revenueByMonth}
                keys={['mtn', 'orange']}
                labels={['MTN MoMo', 'Orange Money']}
                colors={['#fbbf24', '#ff6900']}
              />
            ) : (
              <EmptyState icon="cash-remove" title="Pas encore de paiements" />
            )}
          </ChartCard>
        </View>
        <View style={{ flex: 1, minWidth: 280 }}>
          <ChartCard title="Top 8 documents" subtitle="Les plus achetés" height={280}>
            {loading ? (
              <Loading />
            ) : topDocs.length > 0 ? (
              <BarSimple data={topDocs} color={colors.primary} />
            ) : (
              <EmptyState icon="trophy-outline" title="Aucun achat" />
            )}
          </ChartCard>
        </View>
      </View>

      <View style={styles.chartsRow}>
        <View style={{ flex: 1, minWidth: 280 }}>
          <ChartCard title="Répartition par genre" subtitle="Utilisateurs" height={260}>
            {loading ? (
              <Loading />
            ) : genderBreakdown.length > 0 ? (
              <PieDonut data={genderBreakdown} />
            ) : (
              <EmptyState icon="account-question-outline" title="Pas de données démographiques" />
            )}
          </ChartCard>
        </View>
        <View style={{ flex: 2, minWidth: 320 }}>
          <ChartCard title="Top 8 régions" subtitle="Provenance des utilisateurs" height={260}>
            {loading ? (
              <Loading />
            ) : regionBreakdown.length > 0 ? (
              <BarSimple data={regionBreakdown} color={colors.info} />
            ) : (
              <EmptyState icon="map-marker-question-outline" title="Pas de données" />
            )}
          </ChartCard>
        </View>
      </View>

      <Card style={{ marginTop: spacing.lg }}>
        <View style={styles.summaryRow}>
          <View style={{ flex: 1, minWidth: 240 }}>
            <Text style={styles.summaryTitle}>Synthèse rapide</Text>
            <Text style={styles.summaryText}>
              Sur la période sélectionnée, MyConcours a enregistré{' '}
              <Text style={styles.bold}>{num(stats.totalSales)}</Text> ventes complétées pour un
              chiffre d'affaires de <Text style={styles.bold}>{FCFA(stats.totalRevenue)}</Text>.
              Le panier moyen est de <Text style={styles.bold}>{FCFA(averageBasket)}</Text>. La
              plateforme compte <Text style={styles.bold}>{num(stats.totalUsers)}</Text>{' '}
              utilisateurs au total dont{' '}
              <Text style={styles.bold}>{num(stats.activeUsers)}</Text> actifs.
            </Text>
          </View>
          <View style={{ gap: 6, flexDirection: 'row', flexWrap: 'wrap' }}>
            <Badge variant="primary" icon="account-group">
              {num(stats.totalUsers)} utilisateurs
            </Badge>
            <Badge variant="success" icon="cash-multiple">
              {FCFA(stats.totalRevenue)}
            </Badge>
            <Badge variant="info" icon="file-document-multiple">
              {num(stats.totalDocs)} docs
            </Badge>
          </View>
        </View>
      </Card>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
    marginBottom: spacing.lg,
  },
  chartsRow: {
    flexDirection: 'row',
    gap: spacing.lg,
    marginBottom: spacing.lg,
    flexWrap: 'wrap',
  },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.lg,
    flexWrap: 'wrap',
  },
  summaryTitle: { fontSize: 14, fontWeight: '700', color: colors.text, marginBottom: spacing.sm },
  summaryText: { fontSize: 13, color: colors.textSecondary, lineHeight: 20 },
  bold: { fontWeight: '700', color: colors.text },
});
